import { FormAction } from '@anytag/shared-types';
import { AUTO_MANAGED_COUNTRY_OPTIONS } from '@anytag/shared-constants';
import { useQueryHelper, useTranslateCountryOptions } from '@anytag/shared-hooks';
import { BaseModal, BaseModalProps, Form, ModalBody, ModalFooter } from '@anytag/shared-atoms';
import { SearchMode, useFetchAutoManagedCustomShopeeCampaignsMutation } from '@anytag/graphql';
import { SelectorField, TextField } from '../../rhf-fields';
import { CustomShopeeCampaignsFormValues, CustomShopeeCampaignsFormValuesKeys } from './types';

export type CustomShopeeCampaignsModalProps = BaseModalProps;

export const CustomShopeeCampaignsModal = ({ onClose, ...restProps }: CustomShopeeCampaignsModalProps) => {
  const { t, enqueueSnackbar } = useQueryHelper();
  const { callFetchAutoManagedCustomShopeeCampaigns, loading } = useFetchAutoManagedCustomShopeeCampaignsMutation();
  const allAvailableAutoManagedCountriesTranslated = useTranslateCountryOptions(AUTO_MANAGED_COUNTRY_OPTIONS);

  const handleSubmit: FormAction<CustomShopeeCampaignsFormValues>['onSubmit'] = async ({
    itemId,
    keyword,
    countryId,
    searchMode
  }) => {
    try {
      await callFetchAutoManagedCustomShopeeCampaigns({
        variables: {
          input: {
            countryId,
            searchMode,
            ...(searchMode === SearchMode.KEYWORD_SEARCH ? { keyword } : { itemId })
          }
        }
      });
      enqueueSnackbar(
        t('Request to update campaigns list has been submitted, please wait around 5 min until they are fetched'),
        { variant: 'success' }
      );
      onClose?.();
    } catch (error) {
      enqueueSnackbar(t(error.message), { variant: 'error' });
    }
  };

  return (
    <BaseModal onClose={onClose} title={t('Pull campaigns by offer title from Shopee')} {...restProps} asChild>
      <Form.Provider<CustomShopeeCampaignsFormValues>
        defaultValues={{ keyword: '', countryId: '' }}
        onSubmit={handleSubmit}
      >
        {({ values: { searchMode, countryId, keyword, itemId }, formState: { isSubmitting } }) => {
          const isKeywordSearch = searchMode === SearchMode.KEYWORD_SEARCH;
          const isFilled = searchMode && countryId ? (isKeywordSearch ? !!keyword : !!itemId) : false;

          return (
            <>
              <ModalBody>
                <SelectorField<CustomShopeeCampaignsFormValuesKeys>
                  name="countryId"
                  title={t('Selector.Country')}
                  options={allAvailableAutoManagedCountriesTranslated}
                  required
                />
                <SelectorField<CustomShopeeCampaignsFormValuesKeys>
                  name="searchMode"
                  required
                  title={t('Selector.Search type')}
                  options={[
                    { value: SearchMode.KEYWORD_SEARCH, label: t<string>('Option.Keyword') },
                    { value: SearchMode.ITEM_SEARCH, label: t<string>('Option.Item ID') }
                  ]}
                />
                {isKeywordSearch ? (
                  <TextField<CustomShopeeCampaignsFormValuesKeys>
                    name="keyword"
                    title={t('TextForm.Full offer title')}
                    placeholder={t<string>('Input keyword')}
                    required
                  />
                ) : (
                  <TextField<CustomShopeeCampaignsFormValuesKeys>
                    name="itemId"
                    title={t('Input item ID')}
                    placeholder={t<string>('Item ID')}
                    required
                  />
                )}

                <p className="mb-1">
                  {t(
                    isKeywordSearch
                      ? '*We fetch offer by exact match, please input full offer title'
                      : '*We will fetch offer by Item ID, please copy and input it here'
                  )}
                </p>
                <p>
                  {t('After you click [Run], please wait for around 5 mins')}&nbsp;
                  {t(`If the offer you wanted didn't pulled, try again`)}
                </p>
              </ModalBody>

              <ModalFooter
                cancelButton={{ onClick: onClose }}
                submitButton={{
                  type: 'submit',
                  title: t('Button.Run'),
                  loading: isSubmitting || loading,
                  disabled: !isFilled || isSubmitting || loading
                }}
              />
            </>
          );
        }}
      </Form.Provider>
    </BaseModal>
  );
};
